/* istanbul ignore file */
// libs
import { Routes } from 'react-router-dom';
// components
import AccountContext from 'domain/sales/account/AccountContext';
import { Page404 } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import AccountRelatedContacts from 'domain/sales/account/Show/AccountRelatedContacts';
import AccountRelatedOpportunities from 'domain/sales/account/Show/AccountRelatedOpportunities';
import AccountClientPortal from 'domain/sales/account/Show/AccountClientPortal';
import { Route } from 'framework/routing';
import { ProtectedRouter } from 'infrastructure/routing';
// services
import { READ_TIMELINE } from 'domain/sales/salesService';
import { READ } from 'domain/shared/core/permissions';
import { GlobalSecurityContext, useAuthorization } from 'framework/authorization';
import AccountsListPage from 'domain/sales/account/list';
import {
  CREATE_ACCOUNT,
  DELIVERY_ACCOUNT_READ_CLIENT_PORTAL_USERS,
  READ_ACCOUNT,
  READ_ACCOUNTS_LIST,
  UPDATE_ACCOUNT,
} from './model';
import { CreateAccount, UpdateAccount } from 'domain/sales/account/createupdate';
import DeliveryContext from 'domain/sales/account/DeliveryContext';
import AccountTimelinePage from 'domain/sales/account/Timeline';
import ViewAccountPage from 'domain/sales/account/Show/ViewAccountPage';
import { ACCOUNT_PATH } from 'domain/shared/account';
import { Features } from 'infrastructure/featureToggle';
import useFeatureToggle from 'framework/featureToggle';
import { useAccountRoute } from './accountRouting';

const AccountRouter = () => {
  const { isAuthenticated } = useAuthenticationService();
  const { isGranted } = useAuthorization();
  const { isFeatureEnabled } = useFeatureToggle();
  const {
    singleEntityRoute,
    createRoute: createEntityRoute,
    updateRoute,
    listRoute,
    timelineRoute,
  } = useAccountRoute();

  return (
    <ProtectedRouter basePath={ACCOUNT_PATH}>
      <Routes>
        <Route
          path={listRoute}
          element={
            <GlobalSecurityContext permissionToCheck={READ_ACCOUNTS_LIST}>
              <AccountsListPage />
            </GlobalSecurityContext>
          }
        />

        <Route
          path={createEntityRoute}
          element={
            <GlobalSecurityContext permissionToCheck={CREATE_ACCOUNT}>
              <CreateAccount />
            </GlobalSecurityContext>
          }
        />

        <Route
          path={updateRoute}
          element={
            <AccountContext permissionToCheck={UPDATE_ACCOUNT}>
              <DeliveryContext>
                <UpdateAccount />
              </DeliveryContext>
            </AccountContext>
          }
        />

        <Route
          path={singleEntityRoute}
          element={
            <AccountContext permissionToCheck={READ_ACCOUNT}>
              <ViewAccountPage />
            </AccountContext>
          }
        />

        <Route
          path={timelineRoute}
          element={
            <AccountContext permissionToCheck={READ_TIMELINE}>
              <AccountTimelinePage />
            </AccountContext>
          }
        />

        <Route
          path={`${singleEntityRoute}/related-contacts`}
          element={
            <AccountContext permissionToCheck={READ}>
              <AccountRelatedContacts />
            </AccountContext>
          }
        />

        <Route
          path={`${singleEntityRoute}/related-opportunities`}
          element={
            <AccountContext permissionToCheck={READ}>
              <AccountRelatedOpportunities />
            </AccountContext>
          }
        />

        {isFeatureEnabled(Features.CLIENT_PORTAL) && isGranted(DELIVERY_ACCOUNT_READ_CLIENT_PORTAL_USERS) && (
          <Route
            path={`${singleEntityRoute}/client-portal`}
            element={
              <AccountContext permissionToCheck={READ}>
                <AccountClientPortal />
              </AccountContext>
            }
          />
        )}

        <Route path="*" element={<Page404 isAuthenticated={isAuthenticated} />} />
      </Routes>
    </ProtectedRouter>
  );
};

AccountRouter.getRouterPath = () => '/accounts/*';

export default AccountRouter;
